import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader, MockBadge, StatusBadge, RiskBadge, Spinner, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ArrowUpCircle, RefreshCw, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Compliance() {
  const { user } = useAuth();
  const [queue, setQueue] = useState(null);
  const [active, setActive] = useState(null);
  const [reason, setReason] = useState("");
  const [infoItems, setInfoItems] = useState("");
  const [factors, setFactors] = useState({});
  const [manualRisk, setManualRisk] = useState("");
  const [riskRef, setRiskRef] = useState(null);
  const isCompliance = user.role === "compliance" || user.role === "administrator";

  const load = () => api.get("/compliance/queue").then((r) => setQueue(r.data));
  useEffect(() => { load(); api.get("/onboarding/config").then((r) => setRiskRef(r.data.risk_reference)).catch(() => {}); }, []);
  if (!queue) return <Spinner />;

  const openDetail = async (id) => { const r = await api.get(`/compliance/${id}`); setActive(r.data); setFactors(r.data.risk_factors || {}); setManualRisk(r.data.compliance_risk_rating?.rating || ""); };
  const rescreen = async () => { const r = await api.post(`/compliance/${active.id}/screen`); setActive({ ...active, screening: r.data }); toast.success("Re-screened"); };
  const saveOffice = async () => {
    try { const r = await api.post(`/compliance/${active.id}/office-use`, { risk_factors: factors, risk_rating: manualRisk || null, office_use: active.office_use || {} });
      setActive(r.data); toast.success("Office-use / risk assessment saved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const requestInfo = async () => {
    const items = infoItems.split(",").map((s) => s.trim()).filter(Boolean);
    if (!items.length) return toast.error("Enter at least one item");
    await api.post(`/compliance/${active.id}/request-info`, { items, note: reason });
    toast.success("Information requested"); setInfoItems(""); setReason(""); setActive(null); load();
  };
  const decide = async (action) => {
    if (!reason) return toast.error("A reason is required");
    try { await api.post(`/compliance/${active.id}/decision`, { action, reason }); toast.success(`Application ${action}d`); setReason(""); setActive(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader overline="Risk & Compliance" title="Compliance Review"
        subtitle="Review identity, screening and risk. A potential screening match enters review — it does not establish wrongdoing." testid="compliance-header" />
      <Card className="p-5">
        {queue.length === 0 ? <EmptyState title="Review queue is empty" hint="Submit an onboarding application as the Customer demo account." /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Applicant</TableHead><TableHead>Status</TableHead><TableHead>Screening</TableHead><TableHead>Risk</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {queue.map((a) => (
                <TableRow key={a.id} data-testid={`compliance-row-${a.id}`}>
                  <TableCell className="font-medium">{a.applicant_name || a.contact?.full_name}</TableCell>
                  <TableCell><StatusBadge status={a.status} /></TableCell>
                  <TableCell>{a.screening ? <StatusBadge status={a.screening.status} /> : "—"}</TableCell>
                  <TableCell><RiskBadge rating={a.compliance_risk_rating?.rating} /></TableCell>
                  <TableCell className="text-right"><Button size="sm" variant="outline" onClick={() => openDetail(a.id)} data-testid={`review-btn-${a.id}`}>Review</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Sheet open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" aria-describedby="compliance-detail-desc">
          <span id="compliance-detail-desc" className="sr-only">Compliance application detail, screening and office-use panel</span>
          {active && (
            <>
              <SheetHeader><SheetTitle>Review · {active.applicant_name || active.contact?.full_name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-5 text-sm">
                <Section title="Applicant">
                  <Row k="Name" v={active.contact?.full_name || active.identity?.full_name} />
                  <Row k="Type" v={active.classification?.customer_type} />
                  <Row k="Nationality" v={active.classification?.nationality} />
                  <Row k="Residence" v={active.classification?.residence} />
                  <Row k="Document" v={`${active.identity?.doc_type || ""} ${active.identity?.doc_number || ""}`} />
                  <Row k="Email verified" v={active.otp?.verified ? "Yes (demo)" : "No"} />
                </Section>

                <Section title={<span className="flex items-center gap-2">Identity & Liveness <MockBadge /></span>}>
                  <Row k="OCR extraction" v={active.ocr?.source ? (active.ocr?.mode === "simulated_fallback" ? "Simulated OCR fallback" : "Automated OCR extraction") : "—"} />
                  <Row k="Liveness" v={active.liveness?.status ? <span className="flex items-center gap-2 flex-wrap"><StatusBadge status={active.liveness.status} /><span className="text-[11px] text-amber-700">{active.liveness.label}</span></span> : "—"} />
                  <Row k="Doc verification" v={active.doc_verification ? <span className="flex items-center gap-2 flex-wrap"><StatusBadge status={active.doc_verification.status} /><span className="text-[11px] text-amber-700">{active.doc_verification.label}</span></span> : "—"} />
                  <p className="text-[11px] text-amber-700">A photograph or OCR result is not proof of document authenticity.</p>
                </Section>

                <Section title={<span className="flex items-center gap-2">Sanctions & PEP Screening <MockBadge /></span>}>
                  <div className="flex items-center justify-between mb-2">
                    {active.screening ? <StatusBadge status={active.screening.status} /> : "—"}
                    <Button size="sm" variant="ghost" onClick={rescreen} className="gap-1"><RefreshCw className="h-3.5 w-3.5" /> Re-screen</Button>
                  </div>
                  {active.screening?.result?.matches?.length > 0 ? active.screening.result.matches.map((m, i) => (
                    <div key={i} className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/40 p-2 mb-2">
                      <div className="flex items-center gap-2 font-medium"><AlertTriangle className="h-4 w-4 text-amber-600" /> {m.name} <span className="text-xs font-normal">({m.category})</span></div>
                      <div className="text-xs text-muted-foreground">{m.list} · match score {m.match_score} {m.position ? `· ${m.position}` : ""}</div>
                    </div>
                  )) : <p className="text-xs text-emerald-700">No potential matches.</p>}
                  <p className="text-[11px] text-amber-700">A potential match does not establish wrongdoing; it requires review.</p>
                </Section>

                <Section title="Compliance Risk (separate from appropriateness)">
                  <div className="flex items-center gap-2 mb-1">
                    {active.compliance_risk_rating ? <RiskBadge rating={active.compliance_risk_rating.rating} /> : <span className="text-xs text-muted-foreground">Not yet assessed (manual)</span>}
                  </div>
                  {(active.screening_flags || []).length > 0 && <ul className="list-disc ml-5 text-xs text-amber-700">{active.screening_flags.map((r, i) => <li key={i}>{r}</li>)}</ul>}
                </Section>

                {active.kyc_type !== "corporate" && (
                  <Section title="KYC details (submitted)">
                    <Row k="Annual income (USD)" v={active.financial?.annual_income_usd} />
                    <Row k="Source of wealth" v={(active.financial?.source_of_wealth || []).join(", ")} />
                    <Row k="Source of funds" v={(active.financial?.source_of_funds || []).join(", ")} />
                    <Row k="Net worth (USD)" v={active.networth?.as_on ? `as on ${active.networth.as_on}` : "—"} />
                    <Row k="Beneficial owner" v={active.bo?.is_bo === "no" ? `No — UBO: ${active.ubo?.name || ""}` : "Self"} />
                    <Row k="FATCA US person" v={active.fatca?.us_person} />
                    <Row k="CRS tax residences" v={(active.crs || []).map((r) => r.country).filter(Boolean).join(", ") || "—"} />
                    <Row k="PEP (self-declared)" v={active.personal?.pep} />
                    <Row k="Appropriateness" v={active.appropriateness?.knowledge ? `${active.appropriateness.knowledge} · ${active.appropriateness.purpose || ""}` : "—"} />
                    <Row k="Signature" v={active.signature?.data ? "Acknowledged (verification N/I)" : "—"} />
                  </Section>
                )}

                {isCompliance && (
                  <Section title={<span className="flex items-center gap-2">Office use — manual risk assessment <MockBadge label="STAFF ONLY" /></span>}>
                    <p className="text-[11px] text-amber-700 mb-2">Provisional reference (pending Barjeel): bands Low 20–40 / Medium 41–70 / High 71–100. PEP override: High under factor 2 (PEP) → High overall. No scores are auto-calculated; staff assess each factor and set the final rating.</p>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                      {(riskRef?.factors || []).map((f) => (
                        <div key={f.n} className="border-b border-border pb-2">
                          <div className="text-xs font-medium">{f.n}. {f.name}</div>
                          <div className="text-[10px] text-muted-foreground mb-1">L: {f.low} · M: {f.medium} · H: {f.high}</div>
                          <RadioGroup value={factors[f.n] || ""} onValueChange={(v) => setFactors({ ...factors, [f.n]: v })} className="flex gap-3">
                            {["Low", "Medium", "High"].map((b) => <label key={b} className="flex items-center gap-1 text-[11px] cursor-pointer"><RadioGroupItem value={b} data-testid={`rf-${f.n}-${b}`} /> {b}</label>)}
                          </RadioGroup>
                        </div>
                      ))}
                    </div>
                    <Label className="text-xs mt-3 block">Final risk rating (manual)</Label>
                    <RadioGroup value={manualRisk} onValueChange={setManualRisk} className="flex gap-4 mt-1">
                      {["Low", "Medium", "High"].map((b) => <label key={b} className="flex items-center gap-2 text-sm cursor-pointer"><RadioGroupItem value={b} data-testid={`risk-${b}`} /> {b}</label>)}
                    </RadioGroup>
                    <Button size="sm" variant="outline" className="mt-2" onClick={saveOffice} data-testid="save-office-use">Save office-use & risk</Button>
                  </Section>
                )}

                <Section title="Audit history">
                  {(active.audit || []).map((a, i) => (
                    <div key={i} className="text-xs text-muted-foreground border-l-2 border-border pl-2 py-1">
                      <span className="font-medium text-foreground capitalize">{a.action?.replace(/_/g, " ")}</span> · {a.by} · {new Date(a.ts).toLocaleString()} {a.reason ? `· ${a.reason}` : ""}
                    </div>
                  ))}
                </Section>

                {isCompliance && !["approved", "rejected"].includes(active.status) && (
                  <Section title="Decision">
                    <div className="mb-2">
                      <Label className="text-xs">Request additional info (comma-separated items)</Label>
                      <Input value={infoItems} onChange={(e) => setInfoItems(e.target.value)} placeholder="Proof of address, Source of wealth" className="mt-1" data-testid="info-items" />
                      <Button size="sm" variant="outline" className="mt-2" onClick={requestInfo} data-testid="request-info-btn">Request information</Button>
                    </div>
                    <Label className="text-xs">Reason (recorded with decision)</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" data-testid="decision-reason" />
                    <div className="flex gap-2 mt-3 flex-wrap">
                      <Button size="sm" className="gap-1" onClick={() => decide("approve")} data-testid="approve-btn"><CheckCircle2 className="h-4 w-4" /> Approve</Button>
                      <Button size="sm" variant="destructive" className="gap-1" onClick={() => decide("reject")} data-testid="reject-btn"><XCircle className="h-4 w-4" /> Reject</Button>
                      <Button size="sm" variant="outline" className="gap-1" onClick={() => decide("escalate")} data-testid="escalate-btn"><ArrowUpCircle className="h-4 w-4" /> Escalate</Button>
                    </div>
                  </Section>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Section({ title, children }) { return <div className="rounded-lg border border-border p-3"><div className="overline-label text-gold mb-2">{title}</div>{children}</div>; }
function Row({ k, v }) { return <div className="flex justify-between py-1"><span className="text-muted-foreground">{k}</span><span className="text-right">{v || "—"}</span></div>; }
